import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronLeft, Ruler } from 'lucide-react';
import {
  GARMENT_CATEGORIES,
  MEASUREMENT_UNITS,
  createTemplateSchema,
  type GarmentCategory,
  type MeasurementUnit,
  type ResolvedMeasurementField,
} from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { FieldFormDialog } from '../components/measurements/field-form-dialog.js';
import { TemplateFieldList } from '../components/measurements/template-field-list.js';
import {
  Badge,
  Button,
  useConfirm,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  ListSkeleton,
  useToast,
} from '@tailoring-crm/ui';
import { fieldInUseCount } from '../lib/measurements/templates-api.js';
import {
  useAddField,
  useCreateTemplate,
  useDeleteField,
  useReorderFields,
  useTemplates,
  useUpdateField,
  useUpdateTemplate,
} from '../lib/measurements/use-templates.js';

/**
 * The measurement template editor.
 *
 * This is the screen that makes the product sellable to a shop nobody has met: a blouse in
 * Chennai and a blouse in Surat are the same garment type with different fields, and this is
 * where a shop says which. It runs on the owner's phone, so the garment type is a select
 * rather than a row of tabs, and every control is a full-width tap target.
 */
export function MeasurementTemplatesPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirm = useConfirm();

  const [includeHidden, setIncludeHidden] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [addingField, setAddingField] = useState(false);
  const [editingField, setEditingField] = useState<ResolvedMeasurementField | null>(null);
  const [creatingType, setCreatingType] = useState(false);

  const query = useTemplates({ includeInactive: includeHidden });
  const templates = useMemo(() => query.data?.templates ?? [], [query.data]);
  const shopDefaultUnit = query.data?.shopDefaultUnit ?? 'in';

  // Settle on a garment type as soon as there is one, and never point at a hidden one.
  useEffect(() => {
    if (templates.length === 0) return;
    if (!selectedKey || !templates.some((one) => one.garmentTypeKey === selectedKey)) {
      setSelectedKey(templates[0]!.garmentTypeKey);
    }
  }, [templates, selectedKey]);

  const template = templates.find((one) => one.garmentTypeKey === selectedKey) ?? null;
  const garmentTypeKey = template?.garmentTypeKey ?? '';

  const addField = useAddField(garmentTypeKey);
  const updateField = useUpdateField(garmentTypeKey);
  const reorderFields = useReorderFields(garmentTypeKey);
  const deleteField = useDeleteField(garmentTypeKey);
  const updateTemplate = useUpdateTemplate(garmentTypeKey);
  const createTemplate = useCreateTemplate();

  const groups = useMemo(
    () => [
      ...new Set(
        (template?.fields ?? [])
          .map((field) => field.group)
          .filter((group): group is string => group !== null),
      ),
    ],
    [template],
  );

  /**
   * Deleting a field. The API refuses once anything has measured with it, and that refusal is
   * not an error to apologise for — it is the shop being told the correct alternative, so the
   * catch offers to switch the field off right here.
   */
  const onDeleteField = async (field: ResolvedMeasurementField): Promise<void> => {
    const confirmed = await confirm({
      title: t('measurements.deleteFieldTitle', { label: field.label }),
      description: t('measurements.deleteFieldDescription'),
      confirmLabel: t('measurements.deleteFieldConfirm'),
      destructive: true,
    });
    if (!confirmed) return;

    try {
      await deleteField.mutateAsync(field.key);
      toast({ title: t('measurements.fieldDeleted', { label: field.label }), variant: 'success' });
    } catch (error) {
      const usageCount = fieldInUseCount(error);
      if (usageCount === null) throw error;

      const switchOff = await confirm({
        title: t('measurements.fieldInUseTitle'),
        description: t('measurements.fieldInUse', { count: usageCount }),
        confirmLabel: t('measurements.switchOffInstead'),
      });
      if (!switchOff) return;
      await updateField.mutateAsync({ fieldKey: field.key, patch: { active: false } });
      toast({ title: t('measurements.fieldSaved'), variant: 'success' });
    }
  };

  const busy =
    addField.isPending || updateField.isPending || reorderFields.isPending || deleteField.isPending;

  return (
    <>
      <PageHeader
        title={t('measurements.title')}
        description={t('measurements.description')}
        action={
          <Button variant="outline" onClick={() => setCreatingType(true)}>
            {t('measurements.addGarmentType')}
          </Button>
        }
      />

      <div className="flex flex-col gap-5">
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 self-start text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
          {t('measurements.back')}
        </Link>

        {/* The shop-wide default. One control, and every template and field that has not
            overridden it follows — which is the point of storing the fallback rather than
            copying a resolved unit onto two hundred fields. */}
        <div className="flex flex-col gap-1.5 rounded-lg border border-border p-3">
          <Label htmlFor="shop-default-unit">{t('measurements.shopDefaultUnit')}</Label>
          <select
            id="shop-default-unit"
            value={shopDefaultUnit}
            disabled
            className="h-11 w-full rounded-md border border-input bg-muted px-3 text-base sm:w-64 sm:text-sm"
          >
            {MEASUREMENT_UNITS.map((unit) => (
              <option key={unit} value={unit}>
                {t(`measurements.unit.${unit}`)}
              </option>
            ))}
          </select>
          <p className="text-sm text-muted-foreground">{t('measurements.shopDefaultUnitHint')}</p>
        </div>

        {query.isPending ? (
          <ListSkeleton rows={4} />
        ) : templates.length === 0 ? (
          <EmptyState
            icon={Ruler}
            title={t('measurements.noFields')}
            description={t('measurements.noFieldsHint')}
            action={
              <Button onClick={() => setCreatingType(true)}>
                {t('measurements.addGarmentType')}
              </Button>
            }
          />
        ) : (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="garment-type">{t('measurements.chooseGarmentType')}</Label>
              <select
                id="garment-type"
                value={selectedKey ?? ''}
                onChange={(event) => setSelectedKey(event.target.value)}
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-base sm:max-w-sm sm:text-sm"
              >
                {templates.map((one) => (
                  <option key={one.garmentTypeKey} value={one.garmentTypeKey}>
                    {one.garmentTypeLabel}
                    {one.active ? '' : ` — ${t('measurements.hiddenBadge')}`}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  className="size-4"
                  checked={includeHidden}
                  onChange={(event) => setIncludeHidden(event.target.checked)}
                />
                {t('measurements.showHidden')}
              </label>
            </div>

            {template ? (
              <section className="flex flex-col gap-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="flex items-center gap-2 text-lg font-semibold">
                      {template.garmentTypeLabel}
                      {!template.active ? (
                        <Badge variant="warning">{t('measurements.hiddenBadge')}</Badge>
                      ) : null}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t('measurements.fieldCount', { count: template.fields.length })} ·{' '}
                      {t(`measurements.unit.${template.resolvedDefaultUnit}`)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => setAddingField(true)} disabled={busy}>
                      {t('measurements.addField')}
                    </Button>
                    <Button
                      variant="outline"
                      disabled={updateTemplate.isPending}
                      onClick={() => void updateTemplate.mutateAsync({ active: !template.active })}
                    >
                      {template.active
                        ? t('measurements.hideGarmentType')
                        : t('measurements.showGarmentType')}
                    </Button>
                  </div>
                </div>

                {template.fields.length === 0 ? (
                  <EmptyState
                    icon={Ruler}
                    title={t('measurements.noFields')}
                    description={t('measurements.noFieldsHint')}
                    action={
                      <Button onClick={() => setAddingField(true)}>
                        {t('measurements.addField')}
                      </Button>
                    }
                  />
                ) : (
                  <TemplateFieldList
                    template={template}
                    disabled={busy}
                    onEdit={setEditingField}
                    onDelete={(field) => void onDeleteField(field)}
                    onReorder={(order) => void reorderFields.mutateAsync(order)}
                  />
                )}
              </section>
            ) : null}
          </>
        )}
      </div>

      <FieldFormDialog
        open={addingField}
        onOpenChange={setAddingField}
        inheritedUnit={(template?.resolvedDefaultUnit ?? shopDefaultUnit) as MeasurementUnit}
        groups={groups}
        onSubmit={async (payload) => {
          const saved = await addField.mutateAsync(payload as never);
          toast({
            title: t('measurements.fieldAdded', { label: String(payload.label) }),
            variant: 'success',
          });
          return saved;
        }}
      />

      {editingField ? (
        <FieldFormDialog
          open
          onOpenChange={(open) => !open && setEditingField(null)}
          field={editingField}
          inheritedUnit={(template?.resolvedDefaultUnit ?? shopDefaultUnit) as MeasurementUnit}
          groups={groups}
          onSubmit={async (payload) => {
            const saved = await updateField.mutateAsync({
              fieldKey: editingField.key,
              patch: payload as never,
            });
            toast({ title: t('measurements.fieldSaved'), variant: 'success' });
            setEditingField(null);
            return saved;
          }}
        />
      ) : null}

      <NewGarmentTypeDialog
        open={creatingType}
        onOpenChange={setCreatingType}
        existingTemplates={templates.map((one) => ({
          key: one.garmentTypeKey,
          label: one.garmentTypeLabel,
        }))}
        onSubmit={async (payload) => {
          const created = await createTemplate.mutateAsync(payload);
          setSelectedKey(created.garmentTypeKey);
          toast({
            title: t('measurements.garmentTypeCreated', { label: created.garmentTypeLabel }),
            variant: 'success',
          });
        }}
      />
    </>
  );
}

/** "Like a kurta but shorter" — how a tailor describes a garment type they want to add. */
function NewGarmentTypeDialog({
  open,
  onOpenChange,
  existingTemplates,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingTemplates: readonly { key: string; label: string }[];
  onSubmit: (payload: {
    garmentTypeLabel: string;
    category: GarmentCategory;
    copyFieldsFrom?: string;
  }) => Promise<void>;
}): ReactElement {
  const { t } = useTranslation();
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState<GarmentCategory>('unisex');
  const [copyFrom, setCopyFrom] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLabel('');
    setCategory('unisex');
    setCopyFrom('');
    setError(null);
  }, [open]);

  const submit = async (): Promise<void> => {
    const parsed = createTemplateSchema.safeParse({
      garmentTypeLabel: label,
      category,
      ...(copyFrom ? { copyFieldsFrom: copyFrom } : {}),
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? null);
      return;
    }

    setPending(true);
    try {
      await onSubmit({
        garmentTypeLabel: parsed.data.garmentTypeLabel,
        category: parsed.data.category,
        ...(parsed.data.copyFieldsFrom ? { copyFieldsFrom: parsed.data.copyFieldsFrom } : {}),
      });
      onOpenChange(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('measurements.newGarmentTypeTitle')}</DialogTitle>
          <DialogDescription>{t('measurements.newGarmentTypeDescription')}</DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          noValidate
        >
          {error ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
            >
              {error}
            </p>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-garment-label">{t('measurements.garmentTypeLabel')}</Label>
            <Input
              id="new-garment-label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder={t('measurements.garmentTypeLabelPlaceholder')}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-garment-category">{t('measurements.category')}</Label>
            <select
              id="new-garment-category"
              value={category}
              onChange={(event) => setCategory(event.target.value as GarmentCategory)}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              {GARMENT_CATEGORIES.map((one) => (
                <option key={one} value={one}>
                  {t(`measurements.category_${one}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-garment-copy">{t('measurements.copyFieldsFrom')}</Label>
            <select
              id="new-garment-copy"
              value={copyFrom}
              onChange={(event) => setCopyFrom(event.target.value)}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              <option value="">{t('measurements.copyFieldsFromNone')}</option>
              {existingTemplates.map((one) => (
                <option key={one.key} value={one.key}>
                  {one.label}
                </option>
              ))}
            </select>
            <p className="text-sm text-muted-foreground">{t('measurements.copyFieldsFromHint')}</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default MeasurementTemplatesPage;
